<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
    <div class="text-center mb-12">
      <h1 class="text-3xl font-bold text-gray-900 mb-4">Bienvenue sur Zebu.net</h1>
      <p class="text-lg text-gray-600 max-w-2xl mx-auto">
        "Rejoignez la communauté des Zébuphiles et participez à l'amélioration les conditions de vie des populations rurales vulnérables dans les pays du Sud.
      </p>
    </div>

    <div class="max-w-3xl mx-auto">
      <div class="bg-white rounded-2xl shadow-xl overflow-hidden">
        <!-- En-tête avec lien de connexion -->
        <div class="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 class="text-xl font-semibold text-gray-900">Créer un compte</h2>
          <router-link 
            to="/login"
            class="inline-flex items-center text-zebu-orange hover:text-zebu-orange/90 font-medium"
          >
            Déjà membre ? Se connecter
            <svg class="ml-2 w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
            </svg>
          </router-link>
        </div>

        <div class="p-8">
          <!-- Formulaire de création de compte -->
          <form @submit.prevent="handleSubmit" class="space-y-6">
            <!-- Civilité -->
            <div>
              <label class="text-sm font-medium text-gray-700">Civilité</label>
              <div class="mt-2 space-x-4">
                <label class="inline-flex items-center">
                  <input
                    type="radio"
                    v-model="formData.civility"
                    value="M"
                    class="form-radio text-zebu-orange focus:ring-zebu-orange"
                  />
                  <span class="ml-2">Monsieur</span>
                </label>
                <label class="inline-flex items-center">
                  <input
                    type="radio"
                    v-model="formData.civility"
                    value="Mme"
                    class="form-radio text-zebu-orange focus:ring-zebu-orange"
                  />
                  <span class="ml-2">Madame</span>
                </label>
              </div>
            </div>

            <!-- Nom et Prénom -->
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label for="lastname" class="block text-sm font-medium text-gray-700">Nom</label>
                <input
                  type="text"
                  id="lastname"
                  v-model="formData.lastname"
                  required
                  class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
                />
              </div>
              <div>
                <label for="firstname" class="block text-sm font-medium text-gray-700">Prénom</label>
                <input
                  type="text"
                  id="firstname"
                  v-model="formData.firstname"
                  required
                  class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
                />
              </div>
            </div>

            <!-- Email -->
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                id="email"
                v-model="formData.email"
                required
                class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
              />
            </div>

            <!-- Mot de passe -->
            <div class="space-y-4">
              <div class="relative">
                <label for="password" class="block text-sm font-medium text-gray-700">
                  Mot de passe
                </label>
                <div class="relative mt-1">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    id="password"
                    v-model="formData.password"
                    required
                    class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20 pr-10"
                    @input="checkPasswordStrength"
                  />
                  <button
                    type="button"
                    @click="showPassword = !showPassword"
                    class="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    <svg
                      v-if="showPassword"
                      class="h-5 w-5 text-gray-400 hover:text-gray-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <svg
                      v-else
                      class="h-5 w-5 text-gray-400 hover:text-gray-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Indicateur de force du mot de passe -->
              <div class="space-y-2">
                <div class="flex gap-2">
                  <div 
                    v-for="(level, index) in 4" 
                    :key="index"
                    class="h-2 flex-1 rounded-full transition-all duration-300"
                    :class="[
                      passwordStrength >= level 
                        ? passwordStrengthColors[passwordStrength - 1] 
                        : 'bg-gray-200'
                    ]"
                  ></div>
                </div>
                <p class="text-sm" :class="passwordStrengthTextColor">
                  {{ passwordStrengthText }}
                </p>
              </div>

              <!-- Confirmation du mot de passe -->
              <div class="relative">
                <label for="confirmPassword" class="block text-sm font-medium text-gray-700">
                  Confirmer le mot de passe
                </label>
                <div class="relative mt-1">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    id="confirmPassword"
                    v-model="confirmPassword"
                    required
                    class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20 pr-10"
                    :class="{'border-red-300': !passwordsMatch && confirmPassword}"
                  />
                </div>
                <p v-if="!passwordsMatch && confirmPassword" class="mt-1 text-sm text-red-600">
                  Les mots de passe ne correspondent pas
                </p>
              </div>
            </div>

            <!-- Société (optionnel) -->
            <div>
              <label for="company" class="block text-sm font-medium text-gray-700">
                Société <span class="text-gray-500">(optionnel)</span>
              </label>
              <input
                type="text"
                id="company"
                v-model="formData.company"
                class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
              />
            </div>

            <!-- Adresse -->
            <div>
              <label for="address" class="block text-sm font-medium text-gray-700">Adresse</label>
              <input
                type="text"
                id="address"
                v-model="formData.address"
                required
                class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
              />
            </div>

            <!-- Code postal, Ville et Pays -->
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div>
                <label for="zipcode" class="block text-sm font-medium text-gray-700">Code postal</label>
                <input
                  type="text"
                  id="zipcode"
                  v-model="formData.zipcode"
                  required
                  class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
                />
              </div>
              <div>
                <label for="city" class="block text-sm font-medium text-gray-700">Ville</label>
                <input
                  type="text"
                  id="city"
                  v-model="formData.city"
                  required
                  class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
                />
              </div>
              <div>
                <label for="country" class="block text-sm font-medium text-gray-700">Pays</label>
                <select
                  id="country"
                  v-model="formData.country"
                  required
                  class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
                >
                  <option v-for="country in sortedCountries" :key="country.code" :value="country.code">
                    {{ country.name.trim() }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Téléphones -->
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label for="phone" class="block text-sm font-medium text-gray-700">
                  Téléphone fixe <span class="text-gray-500">(optionnel)</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  v-model="formData.phone"
                  class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
                />
              </div>
              <div>
                <label for="mobile" class="block text-sm font-medium text-gray-700">
                  Téléphone mobile <span class="text-gray-500">(optionnel)</span>
                </label>
                <input
                  type="tel"
                  id="mobile"
                  v-model="formData.mobile"
                  class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-zebu-orange focus:ring focus:ring-zebu-orange/20"
                />
              </div>
            </div>

            <!-- Newsletter et RGPD -->
            <div class="space-y-4">
              <div class="flex items-start">
                <div class="flex items-center h-5">
                  <input
                    id="newsletter"
                    type="checkbox"
                    v-model="formData.newsletter"
                    class="h-4 w-4 rounded border-gray-300 text-zebu-orange focus:ring-zebu-orange"
                  />
                </div>
                <div class="ml-3">
                  <label for="newsletter" class="text-sm text-gray-700">
                    Je souhaite recevoir la newsletter de Zebunet
                  </label>
                </div>
              </div>

              <div class="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                <p>
                  Les informations recueillies font l'objet d'un traitement informatique destiné à la gestion de votre compte.
                  Conformément à la loi "informatique et libertés" du 6 janvier 1978 modifiée, vous bénéficiez d'un droit d'accès,
                  de rectification et de suppression des informations qui vous concernent.
                </p>
              </div>
            </div>

            <div>
              <button
                type="submit"
                :disabled="isProcessing"
                class="w-full bg-zebu-orange text-white rounded-lg px-6 py-4 text-lg font-semibold hover:bg-zebu-orange/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zebu-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
              >
                <div class="flex items-center justify-center">
                  <svg
                    v-if="isProcessing"
                    class="animate-spin -ml-1 mr-3 h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {{ isProcessing ? 'Création du compte...' : 'Créer mon compte' }}
                </div>
              </button>
            </div>
          </form>

          <!-- Message d'erreur -->
          <div v-if="error" class="mt-4 rounded-md bg-red-50 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-red-800">{{ error }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { memberareaService } from '@/services/memberarea.service'
import { publicService } from '@/services/public.service.js'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

const isProcessing = ref(false)
const error = ref(null)
const countries = ref([])
const showPassword = ref(false)
const confirmPassword = ref('')
const passwordStrength = ref(0)

const formData = ref({
  civility: '',
  lastname: '',
  firstname: '',
  email: '',
  password: '',
  company: '',
  address: '',
  zipcode: '',
  city: '',
  country: 'FR',
  phone: '',
  mobile: '',
  newsletter: false
})

const sortedCountries = computed(() => {
  // Séparer la France des autres pays
  const france = countries.value.find(country => country.code === 'FR')
  const otherCountries = countries.value
    .filter(country => country.code !== 'FR')
    .sort((a, b) => a.name.localeCompare(b.name, 'fr', { ignorePunctuation: true }))

  return france ? [france, ...otherCountries] : otherCountries
})

const passwordStrengthColors = [
  'bg-red-500',    // Très faible
  'bg-orange-500', // Faible
  'bg-yellow-500', // Moyen
  'bg-green-500'   // Fort
]

const passwordStrengthText = computed(() => {
  switch (passwordStrength.value) {
    case 0: return 'Entrez un mot de passe'
    case 1: return 'Mot de passe très faible'
    case 2: return 'Mot de passe faible'
    case 3: return 'Mot de passe moyen'
    case 4: return 'Mot de passe fort'
    default: return ''
  }
})

const passwordStrengthTextColor = computed(() => {
  switch (passwordStrength.value) {
    case 1: return 'text-red-600'
    case 2: return 'text-orange-600'
    case 3: return 'text-yellow-600'
    case 4: return 'text-green-600'
    default: return 'text-gray-600'
  }
})

const passwordsMatch = computed(() => {
  if (!confirmPassword.value) return true
  return formData.value.password === confirmPassword.value
})

const checkPasswordStrength = () => {
  const password = formData.value.password
  let strength = 0

  if (password.length >= 8) strength++
  if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++
  if (password.match(/\d/)) strength++
  if (password.match(/[^a-zA-Z\d]/)) strength++

  passwordStrength.value = strength
}

// Charger la liste des pays
const loadCountries = async () => {
  try {
    const response = await memberareaService.getCountries()
    countries.value = response
  } catch (error) {
    console.error('Erreur lors du chargement des pays:', error)
    toast.error("Erreur lors du chargement de la liste des pays")
  }
}

onMounted(async () => {
  await loadCountries()
})

const handleSubmit = async () => {
  if (isProcessing.value) return
  if (!passwordsMatch.value) {
    error.value = 'Les mots de passe ne correspondent pas'
    return
  }
  if (passwordStrength.value < 3) {
    error.value = 'Le mot de passe est trop faible'
    return
  }
  
  isProcessing.value = true
  error.value = ''
  
  try {
    const response = await publicService.register(formData.value)
    
    if (response.success) {
      toast.success('Votre compte a été créé avec succès')
      router.push({
        name: 'register-confirmation',
        params: { email: formData.value.email }
      })
    } else {
      error.value = response.message || "Une erreur est survenue lors de la création du compte"
    }
  } catch (err) {
    error.value = err.response?.data?.message || "Une erreur est survenue lors de la création du compte"
    console.error('Erreur détaillée:', err)
  } finally {
    isProcessing.value = false
  }
}
</script>

<style scoped>
/* Styles de base pour tous les inputs et select */
input[type="text"],
input[type="email"],
input[type="tel"],
input[type="password"],
select {
  @apply mt-1 block w-full rounded-lg border-2 border-gray-200 bg-white px-3
  focus:border-zebu-orange focus:ring-1 focus:ring-zebu-orange/20
  hover:border-gray-300
  transition-all duration-200
  shadow-sm;
  min-height: 42px;
}

/* Style spécifique pour le select */
select {
  @apply pr-10 appearance-none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
}

/* Style pour les radio buttons */
input[type="radio"] {
  @apply w-5 h-5 border-2 border-gray-300 
  text-zebu-orange 
  focus:ring-2 focus:ring-zebu-orange/30 focus:ring-offset-2
  hover:border-zebu-orange
  transition-colors duration-200
  cursor-pointer;
}

/* Style pour la checkbox */
input[type="checkbox"] {
  @apply w-5 h-5 rounded border-2 border-gray-300 
  text-zebu-orange 
  focus:ring-2 focus:ring-zebu-orange/30 focus:ring-offset-2
  hover:border-zebu-orange
  transition-colors duration-200
  cursor-pointer;
}

/* Style pour les labels */
label {
  @apply font-medium text-gray-700;
}

/* Style pour les champs requis - plus subtil */
input:required,
select:required {
  @apply border-l-[3px] border-l-zebu-orange/30;
}

/* Style pour les états disabled */
input:disabled,
select:disabled {
  @apply bg-gray-50 cursor-not-allowed border-gray-200;
}

/* Style pour les états focus */
input:focus,
select:focus {
  @apply border-zebu-orange/70 ring-2 ring-zebu-orange/20 outline-none;
}

/* Style pour les états hover */
input:hover:not(:disabled),
select:hover:not(:disabled) {
  @apply border-gray-300;
}
</style> 